/**
 * Dates and times, one way everywhere.
 *
 * Eighty-odd screens called `toLocaleDateString()` with no locale, so the
 * same day read "9/13/2026" on one page, "Sep 13, 2026" on the next and
 * "2026-09-13" on a third, all in US order for an Indian product. These are
 * fixed to en-IN: "13 Sep 2026" and "13 Sep 2026, 11:55 pm".
 *
 * A missing value renders as an em dash rather than 1 January 1970, which is
 * what `new Date(null)` used to print.
 */
type When = string | number | Date | null | undefined;

const DATE = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const DATE_TIME = new Intl.DateTimeFormat('en-IN', {
  day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit',
});

function valid(v: When): Date | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(v: When): string {
  const d = valid(v);
  return d ? DATE.format(d) : '—';
}

export function formatDateTime(v: When): string {
  const d = valid(v);
  return d ? DATE_TIME.format(d) : '—';
}

const TIME = new Intl.DateTimeFormat('en-IN', { hour: 'numeric', minute: '2-digit' });

/** "10:05 pm" — the time as `formatDateTime` writes it, for a line that already names the day. */
export function formatTime(v: When): string {
  const d = valid(v);
  return d ? TIME.format(d) : '—';
}
